import { useRef, useState } from 'react'
import { AtSign } from 'lucide-react'
import { Button, Form, Notice, TextField } from '@/components/play'
import { fieldErrorsFrom } from '@/components/account/accountErrors'
import type { WatchIntent } from '@/lib/watch/intent'
import {
  USERNAME_CHARACTERS_MESSAGE,
  USERNAME_FORMAT,
  USERNAME_LENGTH_MESSAGE,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  normalizeUsername,
} from '@/lib/profile'
import { useAuth } from '@/state/AuthContext'
import type { UserResource } from '@/types/users'
import { api } from '@/util/api'
import { WatchSheet } from './WatchSheet'

export type UsernameStepProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Runs once the username is saved, with the signed-in user as the API returned it. */
  onDone: (user: UserResource) => void
  /** The intent sign-in will complete, for the button: Start chatting, or Continue to go live. */
  intent?: WatchIntent['kind'] | null
}

const SAVE_FAILED = 'We couldn’t save your username. Try again.'

const ACTIONS: Record<WatchIntent['kind'], string> = { chat: 'Start chatting', go_live: 'Continue', remind: 'Continue' }

/**
 * After signing in, an account without a username picks one before the intent completes: chat and the stage show
 * people by handle. Checked as it's typed; the server's answer (a taken handle) shows on the field.
 */
export function UsernameStep({ open, onOpenChange, onDone, intent = null }: UsernameStepProps) {
  return (
    <WatchSheet open={open} onOpenChange={onOpenChange} title="Your profile">
      <UsernameForm onDone={onDone} action={intent ? ACTIONS[intent] : 'Save'} />
    </WatchSheet>
  )
}

function UsernameForm({ onDone, action }: { onDone: (user: UserResource) => void; action: string }) {
  const { user, updateUser } = useAuth()
  const [value, setValue] = useState(user?.attributes.username ?? '')
  // A too-short handle is flagged once saving was tried, not while the first letters go in.
  const [attempted, setAttempted] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const username = normalizeUsername(value)
  const lengthOk = username.length >= USERNAME_MIN_LENGTH && username.length <= USERNAME_MAX_LENGTH
  const charactersOk = !username || USERNAME_FORMAT.test(username)
  const fieldError =
    serverError ?? (!charactersOk ? USERNAME_CHARACTERS_MESSAGE : attempted && !lengthOk ? USERNAME_LENGTH_MESSAGE : null)

  async function save() {
    if (saving) return
    if (fieldError || !lengthOk) {
      setAttempted(true)
      input.current?.focus()
      return
    }
    setFailed(false)
    setSaving(true)
    try {
      const res = await api.patch<{ data: UserResource }>('/profile', { user: { username } })
      updateUser(res.data)
      onDone(res.data)
    } catch (failure) {
      const taken = fieldErrorsFrom(failure, ['username'])?.username[0]
      if (taken) {
        setServerError(taken)
        input.current?.focus()
      } else setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Form className="watch-sign-in watch-sign-in__username" onFormSubmit={() => void save()}>
      {failed && <Notice tone="error" alert>{SAVE_FAILED}</Notice>}
      <TextField
        ref={input}
        name="username"
        label="Username"
        description="3 to 30 lowercase letters, numbers or underscores."
        icon={<AtSign />}
        autoComplete="nickname"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="done"
        maxLength={USERNAME_MAX_LENGTH}
        value={value}
        onChange={(event) => {
          setValue(event.target.value.replace(/^@+/, '').toLowerCase())
          setServerError(null)
        }}
        error={fieldError ?? undefined}
      />
      <Button type="submit" loading={saving}>{action}</Button>
    </Form>
  )
}
